import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, Users, Download, Trash2, Search, Folder, Plus, FolderOpen, X, ChevronDown, ChevronRight, Edit2 } from 'lucide-react';
import { createFlexibleRepository } from '@/repositories/flexibleEntityRepository';

interface VirtualRoomNote {
  id: number;
  title: string;
  content: string;
  roomTitle?: string;
  roomCategory?: string;
  participants?: string[];
  date: string;
  created_at: string;
  updated_at: string;
  section?: string; // Changed: now called "section" instead of "group"
}

interface CourseSection {
  id: string;
  name: string; // e.g., "COA5628/Health & Wellness"
  color: string;
  count: number;
  isExpanded?: boolean; // Track if section is open/closed
}

const VirtualRoomNotesPage = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<VirtualRoomNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNote, setSelectedNote] = useState<VirtualRoomNote | null>(null);
  const [selectedSection, setSelectedSection] = useState<string>('all'); // 'all', 'unsectioned', or section id
  const [sections, setSections] = useState<CourseSection[]>([]);
  const [showNewSectionModal, setShowNewSectionModal] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionColor, setNewSectionColor] = useState('#8b5cf6'); // purple default

  // Predefined color options
  const sectionColors = [
    { value: '#8b5cf6', label: 'Purple' },
    { value: '#3b82f6', label: 'Blue' },
    { value: '#10b981', label: 'Green' },
    { value: '#f59e0b', label: 'Orange' },
    { value: '#ef4444', label: 'Red' },
    { value: '#ec4899', label: 'Pink' }
  ];

  // Load notes from database
  useEffect(() => {
    loadNotes();
    loadSections();
  }, []);

  const loadSections = () => {
    // Load sections from localStorage
    const savedSections = localStorage.getItem('transcription_sections');
    console.log('[VirtualRoomNotes] Loading sections from localStorage:', savedSections);
    if (savedSections) {
      const parsed = JSON.parse(savedSections);
      // Add isExpanded property if not present
      const sectionsWithExpanded = parsed.map((s: CourseSection) => ({ ...s, isExpanded: s.isExpanded ?? true }));
      console.log('[VirtualRoomNotes] Parsed sections:', sectionsWithExpanded);
      setSections(sectionsWithExpanded);
    } else {
      console.log('[VirtualRoomNotes] No saved sections found');
    }
  };

  const saveSections = (newSections: CourseSection[]) => {
    localStorage.setItem('transcription_sections', JSON.stringify(newSections));
    setSections(newSections);
  };

  const toggleSection = (sectionId: string) => {
    const updated = sections.map(s => 
      s.id === sectionId ? { ...s, isExpanded: !s.isExpanded } : s
    );
    saveSections(updated);
  };

  const createSection = () => {
    if (!newSectionName.trim()) {
      alert('Please enter a section name (e.g., "COA5628/Health & Wellness")');
      return;
    }

    const newSection: CourseSection = {
      id: `section_${Date.now()}`,
      name: newSectionName.trim(),
      color: newSectionColor,
      count: 0,
      isExpanded: true
    };

    saveSections([...sections, newSection]);
    setNewSectionName('');
    setShowNewSectionModal(false);
  };

  const deleteSection = (sectionId: string) => {
    if (!confirm('Delete this section? Transcripts will be moved to "Unsectioned".')) return;
    
    // Remove section assignment from notes
    const updatedNotes = notes.map(note => 
      note.section === sectionId ? { ...note, section: undefined } : note
    );
    setNotes(updatedNotes);
    
    // Save updated notes
    updatedNotes.forEach(note => updateNoteSection(note.id, undefined));
    
    // Remove section
    saveSections(sections.filter(s => s.id !== sectionId));
    if (selectedSection === sectionId) {
      setSelectedSection('all');
    }
  };

  const updateNoteSection = async (noteId: number, sectionId: string | undefined) => {
    try {
      const repo = createFlexibleRepository({ entityType: 'VirtualRoomTranscript' });
      const note = notes.find(n => n.id === noteId);
      if (!note) return;

      // 🔧 FIX: Properly preserve all data fields when updating section
      // The repository expects a `data` object with all the original fields
      await repo.update(noteId, {
        title: note.title, // Preserve title
        transcript: note.content, // Preserve content
        roomTitle: note.roomTitle,
        roomCategory: note.roomCategory,
        participants: note.participants,
        date: note.date,
        section: sectionId, // Update section
        notes: note.content // Some records might use 'notes' instead of 'transcript'
      });

      // Update local state
      setNotes(notes.map(n => 
        n.id === noteId ? { ...n, section: sectionId } : n
      ));
    } catch (error) {
      console.error('Error updating note section:', error);
    }
  };

  const loadNotes = async () => {
    try {
      setLoading(true);
      // Use VirtualRoomTranscript to separate from calendar items
      const repo = createFlexibleRepository({ 
        entityType: 'VirtualRoomTranscript', 
        orderBy: 'updated_at DESC' 
      });
      
      const allNotes = await repo.list();
      
      // Transform to VirtualRoomNote format
      const transformedNotes: VirtualRoomNote[] = allNotes.map((note: any) => ({
        id: note.id,
        title: note.data?.title || note.data?.roomTitle || 'Untitled Session',
        content: note.data?.transcript || note.data?.content || note.data?.notes || '',
        roomTitle: note.data?.roomTitle,
        roomCategory: note.data?.roomCategory || note.data?.category,
        participants: note.data?.participants || [],
        date: note.data?.date || note.created_at,
        created_at: note.created_at,
        updated_at: note.updated_at,
        section: note.data?.section // Load section assignment
      }));
      
      setNotes(transformedNotes);
    } catch (error) {
      console.error('Error loading virtual room notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteNote = async (id: number) => {
    if (!confirm('Delete this virtual room note? This cannot be undone.')) return;
    
    try {
      const repo = createFlexibleRepository({ entityType: 'Transcript' });
      await repo.delete(id);
      setNotes(notes.filter(n => n.id !== id));
      if (selectedNote?.id === id) {
        setSelectedNote(null);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note');
    }
  };

  const downloadNote = (note: VirtualRoomNote) => {
    const categoryLabel = note.roomCategory ? `\n**Session Type:** ${note.roomCategory.charAt(0).toUpperCase() + note.roomCategory.slice(1)}` : '';
    const content = `# ${note.title}\n\n**Date:** ${new Date(note.date).toLocaleDateString()}\n**Room:** ${note.roomTitle || 'N/A'}${categoryLabel}\n**Participants:** ${note.participants?.join(', ') || 'N/A'}\n\n---\n\n${note.content}`;
    
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${note.title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filteredNotes = notes.filter(note => {
    // Filter by section
    if (selectedSection !== 'all') {
      if (selectedSection === 'unsectioned' && note.section) return false;
      if (selectedSection !== 'unsectioned' && note.section !== selectedSection) return false;
    }
    
    // Filter by search query
    return (
      note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.roomTitle?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Calculate section counts
  const sectionCounts = sections.map(section => ({
    ...section,
    count: notes.filter(n => n.section === section.id).length
  }));
  const unsectionedCount = notes.filter(n => !n.section).length;

  console.log('[VirtualRoomNotes] Render - sections:', sections.length, 'sectionCounts:', sectionCounts.length);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0118] via-[#16213e] to-[#0f172a] text-white">
      {/* Header */}
      <div className="bg-black/30 border-b border-white/10 px-4 md:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/organizer')}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Virtual Room Notes</h1>
              <p className="text-sm text-slate-400">AI-generated summaries and transcripts from your sessions</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">{notes.length} notes</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-400">Loading notes...</p>
            </div>
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Virtual Room Notes Yet</h3>
            <p className="text-slate-400 mb-6">Notes from your virtual room sessions will appear here</p>
            <button
              onClick={() => navigate('/organizer')}
              className="px-6 py-3 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sections Sidebar */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-black/30 border border-white/10 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white">Course Sections</h3>
                  <button
                    onClick={() => setShowNewSectionModal(true)}
                    className="p-1 rounded hover:bg-white/10 transition-colors"
                    title="Create new section"
                  >
                    <Plus className="w-4 h-4 text-purple-400" />
                  </button>
                </div>

                {/* All Notes */}
                <button
                  onClick={() => setSelectedSection('all')}
                  className={`w-full flex items-center justify-between p-2 rounded-lg mb-2 transition-colors ${
                    selectedSection === 'all' ? 'bg-purple-600/30 text-purple-200' : 'hover:bg-white/5 text-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    <span className="text-sm font-medium">All Transcripts</span>
                  </div>
                  <span className="text-xs bg-black/30 px-2 py-0.5 rounded-full">{notes.length}</span>
                </button>

                {/* Unsectioned */}
                <button
                  onClick={() => setSelectedSection('unsectioned')}
                  className={`w-full flex items-center justify-between p-2 rounded-lg mb-3 transition-colors ${
                    selectedSection === 'unsectioned' ? 'bg-purple-600/30 text-purple-200' : 'hover:bg-white/5 text-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Folder className="w-4 h-4" />
                    <span className="text-sm font-medium">Unsectioned</span>
                  </div>
                  <span className="text-xs bg-black/30 px-2 py-0.5 rounded-full">{unsectionedCount}</span>
                </button>

                {/* Course Sections with nested transcripts */}
                <div className="space-y-1">
                  {sectionCounts.map(section => {
                    const sectionNotes = notes.filter(n => n.section === section.id);
                    return (
                      <div key={section.id} className="group/item">
                        {/* Section Header */}
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleSection(section.id)}
                            className="p-1 hover:bg-white/5 rounded transition-colors"
                          >
                            {section.isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                          <button
                            onClick={() => setSelectedSection(section.id)}
                            className={`flex-1 flex items-center justify-between p-2 rounded-lg transition-colors ${
                              selectedSection === section.id ? 'bg-purple-600/30 text-purple-200' : 'hover:bg-white/5 text-slate-300'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Folder className="w-4 h-4" style={{ color: section.color }} />
                              <span className="text-sm font-medium truncate">{section.name}</span>
                            </div>
                            <span className="text-xs bg-black/30 px-2 py-0.5 rounded-full">{section.count}</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSection(section.id);
                            }}
                            className="opacity-0 group-hover/item:opacity-100 p-1 rounded hover:bg-red-500/20 transition-all"
                            title="Delete section"
                          >
                            <X className="w-3 h-3 text-red-400" />
                          </button>
                        </div>

                        {/* Nested Transcripts */}
                        {section.isExpanded && sectionNotes.length > 0 && (
                          <div className="ml-6 mt-1 space-y-1">
                            {sectionNotes.map(note => (
                              <button
                                key={note.id}
                                onClick={() => setSelectedNote(note)}
                                className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                                  selectedNote?.id === note.id
                                    ? 'bg-purple-500/20 text-purple-200'
                                    : 'text-slate-400 hover:bg-white/5 hover:text-slate-300'
                                }`}
                              >
                                <div className="truncate">{note.title}</div>
                                <div className="text-[10px] text-slate-500 mt-0.5">
                                  {new Date(note.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Notes List */}
            <div className="lg:col-span-1 space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search notes..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-400"
                />
              </div>

              {/* Notes Cards */}
              <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                {filteredNotes.map((note) => (
                  <button
                    key={note.id}
                    onClick={() => setSelectedNote(note)}
                    className={`w-full text-left p-4 rounded-lg border transition-all ${
                      selectedNote?.id === note.id
                        ? 'bg-purple-600/30 border-purple-400'
                        : 'bg-black/30 border-white/10 hover:bg-black/40 hover:border-white/20'
                    }`}
                  >
                    <h3 className="font-semibold text-white mb-1 truncate">{note.title}</h3>
                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                      <Calendar className="w-3 h-3" />
                      {new Date(note.date).toLocaleDateString()}
                    </div>
                    {note.roomCategory && (
                      <div className="text-xs font-semibold mb-1">
                        <span className={`px-2 py-0.5 rounded ${
                          note.roomCategory === 'discussion' ? 'bg-blue-500/20 text-blue-300' :
                          note.roomCategory === 'assignment' ? 'bg-purple-500/20 text-purple-300' :
                          note.roomCategory === 'experiential' ? 'bg-emerald-500/20 text-emerald-300' :
                          'bg-pink-500/20 text-pink-300'
                        }`}>
                          {note.roomCategory.charAt(0).toUpperCase() + note.roomCategory.slice(1)}
                        </span>
                      </div>
                    )}
                    {note.participants && note.participants.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Users className="w-3 h-3" />
                        {note.participants.length} participants
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Note Detail */}
            <div className="lg:col-span-2">
              {selectedNote ? (
                <div className="bg-black/30 border border-white/10 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-white mb-2">{selectedNote.title}</h2>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(selectedNote.date).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </div>
                        {selectedNote.roomCategory && (
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-lg font-semibold text-sm ${
                              selectedNote.roomCategory === 'discussion' ? 'bg-blue-500/20 text-blue-300' :
                              selectedNote.roomCategory === 'assignment' ? 'bg-purple-500/20 text-purple-300' :
                              selectedNote.roomCategory === 'experiential' ? 'bg-emerald-500/20 text-emerald-300' :
                              'bg-pink-500/20 text-pink-300'
                            }`}>
                              {selectedNote.roomCategory.charAt(0).toUpperCase() + selectedNote.roomCategory.slice(1)} Session
                            </span>
                          </div>
                        )}
                        {selectedNote.roomTitle && (
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            {selectedNote.roomTitle}
                          </div>
                        )}
                        {selectedNote.participants && selectedNote.participants.length > 0 && (
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            {selectedNote.participants.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Section Assignment Dropdown */}
                      <select
                        value={selectedNote.section || 'unsectioned'}
                        onChange={(e) => updateNoteSection(selectedNote.id, e.target.value === 'unsectioned' ? undefined : e.target.value)}
                        className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-sm focus:outline-none focus:border-purple-400"
                      >
                        <option value="unsectioned">Unsectioned</option>
                        {sections.map(section => (
                          <option key={section.id} value={section.id}>{section.name}</option>
                        ))}
                      </select>
                      
                      <button
                        onClick={() => downloadNote(selectedNote)}
                        className="p-2 rounded-lg bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteNote(selectedNote.id)}
                        className="p-2 rounded-lg bg-red-500/20 text-red-200 hover:bg-red-500/30 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="prose prose-invert max-w-none">
                    <div className="bg-black/20 rounded-lg p-4 border border-white/5">
                      <pre className="whitespace-pre-wrap text-sm text-slate-200 font-sans leading-relaxed">
                        {selectedNote.content || 'No content available'}
                      </pre>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-black/30 border border-white/10 rounded-lg p-6 flex items-center justify-center h-full min-h-[400px]">
                  <div className="text-center text-slate-400">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Select a note to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* New Section Modal */}
      {showNewSectionModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4">Create New Course Section</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Section Name</label>
                <input
                  type="text"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  placeholder="e.g. COA5628/Health & Wellness"
                  className="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-400"
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-1">Use format: CourseCode/Course Name</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Color</label>
                <div className="grid grid-cols-3 gap-2">
                  {sectionColors.map(color => (
                    <button
                      key={color.value}
                      onClick={() => setNewSectionColor(color.value)}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        newSectionColor === color.value
                          ? 'border-white scale-105'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: `${color.value}30`, color: color.value }}
                    >
                      {color.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-4">
                <button
                  onClick={createSection}
                  className="flex-1 px-4 py-2 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition-colors"
                >
                  Create Section
                </button>
                <button
                  onClick={() => {
                    setShowNewSectionModal(false);
                    setNewSectionName('');
                    setNewSectionColor('#8b5cf6');
                  }}
                  className="flex-1 px-4 py-2 rounded-lg bg-black/30 text-slate-300 font-semibold hover:bg-black/40 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VirtualRoomNotesPage;
